import React, { useState, useEffect } from 'react';
import Layout from './Layout';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { useAuth } from '../App';
import api from '../utils/api';
import { Bell, Plus, Edit, Trash2, Power, PowerOff, AlertCircle, Info } from 'lucide-react';

const NotificationSettings = () => {
  const { user } = useAuth();
  const [rules, setRules] = useState([]);
  const [events, setEvents] = useState([]);
  const [recipientTypes, setRecipientTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewRule, setShowNewRule] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [newRule, setNewRule] = useState({
    name: '',
    description: '',
    event: '',
    recipient_types: [],
    title_template: '',
    body_template: '',
    is_active: true,
    priority: 1,
    require_interaction: false
  });

  useEffect(() => {
    fetchRules();
    fetchEvents();
    fetchRecipientTypes();
  }, []);

  const fetchRules = async () => {
    try {
      const response = await api.get('/notification-rules');
      setRules(response.data);
    } catch (error) {
      console.error('Error fetching notification rules:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await api.get('/notification-events');
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const fetchRecipientTypes = async () => {
    try {
      const response = await api.get('/notification-recipient-types');
      setRecipientTypes(response.data);
    } catch (error) {
      console.error('Error fetching recipient types:', error);
    }
  };

  const handleCreateRule = async () => {
    try {
      await api.post('/notification-rules', newRule);
      alert('Regola di notifica creata con successo!');
      setShowNewRule(false);
      resetNewRule();
      fetchRules();
    } catch (error) {
      console.error('Error creating rule:', error);
      alert('Errore nella creazione della regola');
    }
  };

  const handleUpdateRule = async () => {
    try {
      await api.put(`/notification-rules/${editingRule.id}`, newRule);
      alert('Regola aggiornata con successo!');
      setEditingRule(null);
      resetNewRule();
      fetchRules();
    } catch (error) {
      console.error('Error updating rule:', error);
      alert('Errore nell\'aggiornamento della regola');
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa regola?')) {
      return;
    }

    try {
      await api.delete(`/notification-rules/${ruleId}`);
      alert('Regola eliminata con successo!');
      fetchRules();
    } catch (error) {
      console.error('Error deleting rule:', error);
      alert('Errore nell\'eliminazione della regola');
    }
  };

  const handleToggleActive = async (rule) => {
    try {
      await api.put(`/notification-rules/${rule.id}`, {
        is_active: !rule.is_active
      });
      fetchRules();
    } catch (error) {
      console.error('Error toggling rule:', error);
      alert('Errore nell\'aggiornamento dello stato');
    }
  };

  const resetNewRule = () => {
    setNewRule({
      name: '',
      description: '',
      event: '',
      recipient_types: [],
      title_template: '',
      body_template: '',
      is_active: true,
      priority: 1,
      require_interaction: false
    });
  };

  const handleEditRule = (rule) => {
    setNewRule({
      name: rule.name,
      description: rule.description || '',
      event: rule.event,
      recipient_types: rule.recipient_types || [],
      title_template: rule.title_template,
      body_template: rule.body_template,
      is_active: rule.is_active,
      priority: rule.priority,
      require_interaction: rule.require_interaction
    });
    setEditingRule(rule);
  };

  const toggleRecipientType = (type) => {
    if (newRule.recipient_types.includes(type)) {
      setNewRule({
        ...newRule,
        recipient_types: newRule.recipient_types.filter(t => t !== type)
      });
    } else {
      setNewRule({
        ...newRule,
        recipient_types: [...newRule.recipient_types, type]
      });
    }
  };

  if (loading) {
    return <Layout title="Gestisci Notifiche"><div>Caricamento...</div></Layout>;
  }

  return (
    <Layout title="Gestisci Notifiche">
      <div className="space-y-6">
        {/* Header */}
        <Card className="p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Bell className="h-6 w-6" />
                Sistema di Notifiche Automatiche
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                Configura regole per inviare notifiche automatiche basate su eventi
              </p>
            </div>
            <Button onClick={() => setShowNewRule(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Nuova Regola
            </Button>
          </div>
        </Card>

        {/* Info Box */}
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="flex gap-3">
            <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900">
              <p className="font-semibold mb-1">Come funziona:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Scegli un <strong>evento</strong> che triggerà la notifica (es: nuova prenotazione)</li>
                <li>Seleziona i <strong>destinatari</strong> per ruolo (admin, responsabili, ospiti, ecc.)</li>
                <li>Personalizza <strong>titolo e messaggio</strong> usando variabili dinamiche</li>
                <li>Le notifiche vengono inviate automaticamente quando l'evento si verifica</li>
              </ul>
            </div>
          </div>
        </Card>

        {/* Rules List */}
        <Card className="p-6">
          <h3 className="text-lg font-bold mb-4">Regole Attive ({rules.filter(r => r.is_active).length}/{rules.length})</h3>
          
          {rules.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Bell className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Nessuna regola configurata</p>
              <p className="text-sm">Crea la tua prima regola per iniziare!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {rules.map((rule) => (
                <div key={rule.id} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold">{rule.name}</h4>
                        {rule.is_active ? (
                          <Badge className="bg-green-100 text-green-800">Attiva</Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-800">Disattivata</Badge>
                        )}
                        <Badge className="bg-blue-100 text-blue-800">
                          Priorità {rule.priority}
                        </Badge>
                      </div>
                      {rule.description && (
                        <p className="text-sm text-slate-600 mb-2">{rule.description}</p>
                      )}
                      <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                        <div>
                          <span className="font-medium">Evento:</span>
                          <div className="text-slate-600 mt-1">
                            {events.find(e => e.value === rule.event)?.label || rule.event}
                          </div>
                        </div>
                        <div>
                          <span className="font-medium">Destinatari:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {rule.recipient_types?.map(type => (
                              <Badge key={type} variant="outline" className="text-xs">
                                {recipientTypes.find(rt => rt.value === type)?.label || type}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <div className="col-span-2">
                          <span className="font-medium">Messaggio:</span>
                          <div className="text-slate-600 mt-1 bg-slate-50 p-2 rounded text-xs">
                            <strong>{rule.title_template}</strong>
                            <br />
                            {rule.body_template}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleActive(rule)}
                        title={rule.is_active ? "Disattiva" : "Attiva"}
                      >
                        {rule.is_active ? (
                          <Power className="h-4 w-4 text-green-600" />
                        ) : (
                          <PowerOff className="h-4 w-4 text-gray-400" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditRule(rule)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteRule(rule.id)}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={showNewRule || editingRule !== null} onOpenChange={(open) => {
          if (!open) {
            setShowNewRule(false);
            setEditingRule(null);
            resetNewRule();
          }
        }}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingRule ? 'Modifica Regola di Notifica' : 'Nuova Regola di Notifica'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              {/* Nome e Descrizione */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nome Regola *</Label>
                  <Input
                    value={newRule.name}
                    onChange={(e) => setNewRule({...newRule, name: e.target.value})}
                    placeholder="es: Notifica nuova prenotazione admin"
                  />
                </div>
                <div>
                  <Label>Priorità</Label>
                  <Select
                    value={newRule.priority.toString()}
                    onValueChange={(value) => setNewRule({...newRule, priority: parseInt(value)})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 - Bassa</SelectItem>
                      <SelectItem value="2">2 - Normale</SelectItem>
                      <SelectItem value="3">3 - Media</SelectItem>
                      <SelectItem value="4">4 - Alta</SelectItem>
                      <SelectItem value="5">5 - Urgente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Descrizione (opzionale)</Label>
                <Input
                  value={newRule.description}
                  onChange={(e) => setNewRule({...newRule, description: e.target.value})}
                  placeholder="Descrizione breve della regola"
                />
              </div>

              {/* Evento */}
              <div>
                <Label>Evento Trigger *</Label>
                <Select
                  value={newRule.event}
                  onValueChange={(value) => setNewRule({...newRule, event: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona evento" />
                  </SelectTrigger>
                  <SelectContent>
                    {events.map(event => (
                      <SelectItem key={event.value} value={event.value}>
                        {event.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Evento che attiverà l'invio della notifica
                </p>
              </div>

              {/* Destinatari */}
              <div>
                <Label>Destinatari *</Label>
                <div className="border rounded-lg p-4 space-y-2 bg-slate-50">
                  {recipientTypes.map(type => (
                    <div key={type.value} className="flex items-center space-x-2">
                      <Checkbox
                        id={`recipient-${type.value}`}
                        checked={newRule.recipient_types.includes(type.value)}
                        onCheckedChange={() => toggleRecipientType(type.value)}
                      />
                      <Label htmlFor={`recipient-${type.value}`} className="cursor-pointer">
                        {type.label}
                      </Label>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Seleziona chi riceverà la notifica
                </p>
              </div>

              {/* Template Titolo */}
              <div>
                <Label>Titolo Notifica *</Label>
                <Input
                  value={newRule.title_template}
                  onChange={(e) => setNewRule({...newRule, title_template: e.target.value})}
                  placeholder="es: 🆕 Nuova Prenotazione"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Puoi usare variabili: {'{{'}property_name{'}}'},  {'{{'}guest_name{'}}'},  {'{{'}booking_code{'}}'}
                </p>
              </div>

              {/* Template Corpo */}
              <div>
                <Label>Messaggio Notifica *</Label>
                <textarea
                  className="w-full border rounded-md p-2 min-h-[100px]"
                  value={newRule.body_template}
                  onChange={(e) => setNewRule({...newRule, body_template: e.target.value})}
                  placeholder="es: Hai ricevuto una nuova prenotazione per {{property_name}} da {{guest_name}} ({{booking_code}})"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Variabili disponibili: property_name, guest_name, booking_code, check_in, check_out, total_amount, platform
                </p>
              </div>

              {/* Opzioni */}
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is-active"
                    checked={newRule.is_active}
                    onCheckedChange={(checked) => setNewRule({...newRule, is_active: checked})}
                  />
                  <Label htmlFor="is-active" className="cursor-pointer">
                    Regola attiva
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="require-interaction"
                    checked={newRule.require_interaction}
                    onCheckedChange={(checked) => setNewRule({...newRule, require_interaction: checked})}
                  />
                  <Label htmlFor="require-interaction" className="cursor-pointer">
                    Richiedi interazione utente (notifica persistente)
                  </Label>
                </div>
              </div>

              {/* Pulsanti */}
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowNewRule(false);
                    setEditingRule(null);
                    resetNewRule();
                  }}
                >
                  Annulla
                </Button>
                <Button
                  onClick={editingRule ? handleUpdateRule : handleCreateRule}
                  disabled={!newRule.name || !newRule.event || newRule.recipient_types.length === 0 || !newRule.title_template || !newRule.body_template}
                >
                  {editingRule ? 'Aggiorna Regola' : 'Crea Regola'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default NotificationSettings;
